import { formatPublicSourceLabel } from "@/features/machines/addressing";
import type { DashboardWidget, ResolvedWidgetValue } from "../../types";
import { formatWidgetNumber, formatWidgetRelative } from "./format";
import { HeroCard, HeroMeaning, type HeroTone } from "./HeroCard";
import { effectiveQuality } from "./quality";
import { describeReadingState, describeThreshold } from "./readingMeaning";

interface GaugeWidgetProps {
  widget: DashboardWidget;
  value: ResolvedWidgetValue;
  editable?: boolean;
  onConfigure?: () => void;
  onGenerateChart?: () => void;
}

interface Point {
  x: number;
  y: number;
}

function polarToCartesian(cx: number, cy: number, radius: number, angleRad: number): Point {
  return {
    x: cx + radius * Math.cos(angleRad),
    y: cy + radius * Math.sin(angleRad),
  };
}

/**
 * Posição do valor na faixa, de 0 a 1.
 *
 * Faixa degenerada (`max <= min`) devolve `null` — a mesma regra do tanque, e
 * pelo mesmo motivo. O código anterior fazia `max - min || 1`: com uma faixa
 * inválida ele inventava um span de 1 unidade e desenhava uma agulha que
 * parecia medida. Um ponteiro apontando para algum lugar é a afirmação mais
 * confiante que este card sabe fazer; fazê-la sem escala é mentir.
 */
export function gaugeFraction(
  value: number | null,
  min: number,
  max: number,
): number | null {
  if (value === null || !Number.isFinite(value)) return null;
  if (!Number.isFinite(min) || !Number.isFinite(max) || max <= min) return null;
  return Math.max(0, Math.min(1, (value - min) / (max - min)));
}

/**
 * Gauge semicircular compacto. Trilha cinza + arco até a porcentagem
 * normalizada + agulha + ticks. Cores via CSS variables.
 *
 * `fraction: null` desenha só a trilha: sem valor utilizável não há arco nem
 * agulha.
 */
function MiniGauge({
  fraction,
  danger,
}: {
  fraction: number | null;
  danger?: boolean;
}) {
  const w = 88;
  const h = 54;
  const cx = w / 2;
  const cy = h - 4;
  const r = 38;
  const pct = fraction ?? 0;
  const angle = Math.PI + Math.PI * pct;
  const tip = polarToCartesian(cx, cy, r, angle);
  const trackD = `M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx + r} ${cy}`;
  const valD = `M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${tip.x} ${tip.y}`;
  const stroke = danger ? "var(--warn-500)" : "var(--brand-600)";

  return (
    <svg className="gauge-svg" width={w} height={h} viewBox={`0 0 ${w} ${h}`} aria-hidden="true">
      <path d={trackD} fill="none" stroke="var(--ink-100)" strokeWidth="6" strokeLinecap="round" />
      {fraction !== null ? (
        <path d={valD} fill="none" stroke={stroke} strokeWidth="6" strokeLinecap="round" />
      ) : null}
      {[0, 0.25, 0.5, 0.75, 1].map((t, i) => {
        const a = Math.PI + Math.PI * t;
        const r1 = r - 9;
        const r2 = r - 1;
        return (
          <line
            key={i}
            x1={cx + r1 * Math.cos(a)}
            y1={cy + r1 * Math.sin(a)}
            x2={cx + r2 * Math.cos(a)}
            y2={cy + r2 * Math.sin(a)}
            stroke="var(--ink-300)"
            strokeWidth="1.2"
            strokeLinecap="round"
          />
        );
      })}
      {fraction !== null ? (
        <line
          x1={cx}
          y1={cy}
          x2={tip.x}
          y2={tip.y}
          stroke="var(--ink-900)"
          strokeWidth="1.6"
          strokeLinecap="round"
        />
      ) : null}
      <circle cx={cx} cy={cy} r="3" fill="var(--ink-900)" />
      <circle cx={cx} cy={cy} r="1.2" fill="#fff" />
    </svg>
  );
}

/**
 * Gauge no cartão herói: o valor é o herói; o arco semicircular é a FACE
 * (posição na escala min/max). Silêncio quando OK; só fala num limite.
 */
export function GaugeWidget({
  widget,
  value,
  editable,
  onConfigure,
  onGenerateChart,
}: GaugeWidgetProps) {
  const min = widget.settings.min ?? 0;
  const max = widget.settings.max ?? 100;
  const decimals = widget.settings.decimals ?? 1;
  const unit = widget.settings.unit?.trim() || null;
  const available = value.isAvailable && effectiveQuality(value) === "live";
  const danger = available && (value.tone === "warn" || value.tone === "danger");
  const fraction = available ? gaugeFraction(value.numericValue, min, max) : null;
  // Ha leitura, mas nao ha escala em que posiciona-la. Diz o que houve, em vez
  // de deixar a agulha parada no zero — que e indistinguivel de uma medida.
  const rangeBroken = available && value.numericValue !== null && fraction === null;
  // FORA DA FAIXA nao e erro de leitura: e informacao. O numero real aparece, o
  // ponteiro satura, e uma frase diz o que aconteceu — mesma regra do tanque.
  //
  // Sem isto o card ficava mudo no caso mais comum do Modbus: o `/meta` de um
  // multimedidor raramente declara faixa, o editor sugere 0-100, e uma tensao
  // de 222 V nascia com o ponteiro cravado no fundo de escala. Um ponteiro
  // cheio e uma afirmacao — "esta no maximo do que este sensor mede" — e ali
  // ela era falsa.
  const outOfRange =
    available && value.numericValue !== null && !rangeBroken
      ? value.numericValue > max
        ? "above"
        : value.numericValue < min
          ? "below"
          : null
      : null;
  const meaning = describeReadingState(value) ?? resolveScaleMeaning(rangeBroken, outOfRange) ?? describeThreshold(widget, value);
  const cardTone: "ok" | "warn" | "danger" =
    meaning?.tone === "danger" || (available && value.tone === "danger")
      ? "danger"
      : (available && value.tone === "warn") || meaning?.tone === "warn"
        ? "warn"
        : "ok";
  const caption = formatPublicSourceLabel(widget.source, widget.settings.signalKind);
  const readout = available ? formatWidgetNumber(value.numericValue, decimals) : "--";

  return (
    <HeroCard
      title={widget.title}
      caption={caption !== widget.title ? caption : null}
      tone={cardTone}
      alarm={widget.settings.alarmEnabled}
      editable={editable}
      onConfigure={onConfigure}
      onGenerateChart={onGenerateChart}
      footnote={available ? `Atualizado ${formatWidgetRelative(value.measuredAt) ?? "agora"}` : null}
    >
      <div className="w-hero__value">
        {readout}
        {unit ? <span className="w-hero__unit">{unit}</span> : null}
      </div>

      {meaning ? <HeroMeaning tone={meaning.tone}>{meaning.text}</HeroMeaning> : null}

      <div
        className="w-hero__gauge"
        role="img"
        aria-label={
          rangeBroken
            ? `${widget.title}: ${readout}, sem escala utilizável`
            : `${widget.title}: ${readout}`
        }
      >
        <MiniGauge fraction={fraction} danger={danger} />
        {/* A regua usa as MESMAS casas do readout. Com 0 fixo, um sensor de
            0–1,5 bar exibia "2" no topo: o numero dizia 1,4 e a regua dizia que
            o maximo era 2, sugerindo uma folga que nao existe. */}
        <div className="bar-scale">
          <span>{formatWidgetNumber(min, decimals)}</span>
          <span>{formatWidgetNumber(max, decimals)}</span>
        </div>
      </div>
    </HeroCard>
  );
}

function resolveScaleMeaning(
  rangeBroken: boolean,
  outOfRange: "above" | "below" | null,
): { tone: HeroTone; text: string } | null {
  // Mesma frase do tanque: a faixa é do card, e o card diz quando ela não serve.
  if (rangeBroken) {
    return { tone: "warn", text: "Faixa inválida: máximo precisa ser maior que o mínimo" };
  }
  if (outOfRange === "above") return { tone: "warn", text: "Acima do máximo da faixa" };
  if (outOfRange === "below") return { tone: "warn", text: "Abaixo do mínimo da faixa" };
  return null;
}
